<template>
  <v-card style="display: flex; flex-direction: column; max-height: 90vh;">
    <block v-if="loading"></block>
    <v-card-title class="font-weight-bold text-nowrap">Creación de Partidas Contables de abonos</v-card-title>
    <div style="overflow-y: auto; padding: 0 16px;">
      <div style="flex: 1; padding: 16px;">
        <v-alert type="info" outlined>
          <strong>Información:</strong> Configure las partidas contables que se generarán para el abono realizado.
          Las partidas se crearán automáticamente basadas en la configuración de cuentas seleccionada.
          <br><br>
          <v-icon color="info" small>mdi-information</v-icon>
          <em>La vista previa se actualiza automáticamente al cambiar cualquier parámetro de configuración.</em>
        </v-alert>
      </div>
  
      <!-- Configuración general de partidas -->
      <div class="mb-3">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="accountingEntryConfig.concept"
              label="Concepto de la partida"
              outlined
              dense
              hint="Concepto general que se aplicará a todas las partidas generadas"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="accountingEntryConfig.type"
              :items="TypesItems"
              item-text="accounting_entry_name"
              item-value="id_accounting_entry_type"
              label="Tipo de partida"
              outlined
              dense
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <DatePicker
              :date="selectedAccountingEntryDate"
              @dateSelected="changeAccountingDate"
              label="Fecha de la partida"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="accountingEntryConfig.nameType"
              :items="accountingNameOptions"
              item-text="text"
              item-value="value"
              label="Nombre de las partidas contables"
              outlined
              dense
              hint="Define cómo se nombrarán los elementos de las partidas contables"
              persistent-hint
            ></v-select>
          </v-col>
        </v-row>
      </div>
  
      <!-- Alert informativo para nombres personalizados -->
      <div v-if="accountingEntryConfig.nameType === 'empty'">
        <v-alert
          :type="emptyItemsCount > 0 ? 'warning' : 'info'"
          outlined
          dense
        >
          <div class="d-flex align-center">
            <v-icon class="mr-2" :color="emptyItemsCount > 0 ? 'warning' : 'info'">
              {{ emptyItemsCount > 0 ? 'mdi-alert-circle' : 'mdi-information' }}
            </v-icon>
            <div>
              <strong>Nombres personalizados:</strong>
              <span v-if="emptyItemsCount > 0" class="error--text">
                {{ emptyItemsCount }} item(s) sin nombre. Complete todos los nombres antes de continuar.
              </span>
              <span v-else class="success--text">
                Todos los items tienen nombres asignados.
              </span>
            </div>
          </div>
        </v-alert>
      </div>
  
      <!-- Vista previa de la estructura de partidas -->
      <div v-if="accountingEntriesPreview.length > 0">
        <v-expansion-panels v-model="previewPanel" multiple>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <strong>Vista previa de partidas contables ({{ accountingEntriesPreview.length }} partida(s))</strong>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-for="(entry, entryIndex) in accountingEntriesPreview" :key="entryIndex" class="mb-4">
                <v-card outlined>
                  <v-card-subtitle>
                    <strong>Partida {{ entryIndex + 1 }}</strong>
                    <br>Concepto: {{ entry.concept }}
                    <br>Fecha: {{ entry.date }}
                    <br>Total Debe: ${{ entry.totalDebe.toFixed(2) }}
                    <br>Total Haber: ${{ entry.totalHaber.toFixed(2) }}
                  </v-card-subtitle>
                  <v-card-text>
                    <v-data-table
                      :headers="accountingEntryHeaders"
                      :items="entry.details"
                      :item-class="getRowClass"
                      dense
                      hide-default-footer
                      disable-pagination
                    >
                      <template v-slot:item.concept="{ item }">
                        <div v-if="accountingEntryConfig.nameType === 'empty'" class="d-flex align-center">
                          <v-text-field
                            v-model="item.concept"
                            dense
                            outlined
                            hide-details
                            @blur="updateCustomItemName(item, item.concept)"
                            placeholder="Nombre personalizado"
                            style="min-width: 200px;"
                            :error="!item.concept || !item.concept.trim()"
                          ></v-text-field>
                          <v-icon
                            v-if="!item.concept || !item.concept.trim()"
                            color="error"
                            class="ml-2"
                            small
                            title="Campo requerido"
                          >
                            mdi-alert-circle
                          </v-icon>
                        </div>
                        <span v-else>{{ item.concept }}</span>
                      </template>
                      <template v-slot:item.Deb="{ item }">
                        ${{ parseFloat(item.Deb || 0).toFixed(2) }}
                      </template>
                      <template v-slot:item.Hab="{ item }">
                        ${{ parseFloat(item.Hab || 0).toFixed(2) }}
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

    </div>
    

    <v-card-actions style="padding: 1rem; justify-content: space-between; flex-shrink: 0;">
      <v-btn class="btncontinue" @click="cancelForm">Cancelar</v-btn>
      <v-btn class="btncontinue" @click="createAccountingEntry" :disabled="loading || !isFormValid">Crear partida</v-btn>
    </v-card-actions>

    <alerts
      v-if="alert.show"
      v-on:close_alert="closeAlert"
      v-on:accept_alert="acceptAlert"
      :properties="alert"
    ></alerts>
  </v-card>
</template>

<script>
import DatePicker from "@/components/DatePicker";
import moment from 'moment';
import { formatDateV2 } from "../../../helpers/formatDate";
import Block from "@/components/Block";
import Alerts from '@/components/Alerts.vue';

export default {
  name: "PayableAccountingForm",
  props: {
    paymentsData: {
      type: Array,
      default: () => []
    },
    provider: {
      type: Object,
      required: true
    },
    branchAccountsConfig: {
      type: Object,
      default: () => ({})
    },
    userData: {
      type: Object,
      default: () => ({})
    },
    selectedPaymentMethod: {
      type: String,
      default: 'Efectivo'
    },
    accountingEntryDate: {
      type: String,
      default: () => moment().format("DD/MM/YYYY")
    },
    abonoId: {
      type: Number,
      default: null
    },
    idReceipt: {
      type: Number,
      default: null
    }
  },
  components: {
    DatePicker,
    Block,
    Alerts
  },
  data() {
    return {
      loading: false,
      alert: {
        type: "success",
        show: false,
        header: "",
        body: "",
      },
      accountingEntryConfig: {
        concept: "",
        type: null,
        nameType: "correlativoCodigo"
      },
      customItemNames: {},
      selectedAccountingEntryDate: null,
      accountingEntriesPreview: [],
      previewPanel: [0],
      closeAfterAcceptAlert: false,
      TypesItems: [],
      branchAccountConfig: null,
      paymentMethods: [],
      AcountList: [],
      accountingEntryHeaders: [
        { text: 'Cuenta', value: 'account_code' },
        { text: 'Descripción', value: 'account_description' },
        { text: 'Concepto', value: 'concept' },
        { text: 'Debe', value: 'Deb' },
        { text: 'Haber', value: 'Hab' }
      ],
      accountingNameOptions: [
        { text: "Mantenerlo vacío", value: "empty" },
        { text: "Correlativo o Código de Generación", value: "correlativoCodigo" },
        { text: "Nombre de proveedor", value: "nombreProveedor" },
        { text: "Mismo concepto de partida", value: "concept" }
      ],
      detailTypes: []
    };
  },
  async mounted() {
    this.moment = moment;
    this.selectedAccountingEntryDate = this.accountingEntryDate;
    await this.getAcounteringType();
    await this.getBranchAccountConfig();
    await this.getAcountList();
    await this.getDetailTypes();
    //await this.getProviderAccount();
    this.generateAccountingEntriesPreview();
  },
  watch: {
    'accountingEntryConfig.concept': 'generateAccountingEntriesPreview',
    'accountingEntryConfig.type': 'generateAccountingEntriesPreview',
    'accountingEntryConfig.nameType': 'generateAccountingEntriesPreview',
    customItemNames: 'generateAccountingEntriesPreview',
    accountingEntryDate() {
      this.selectedAccountingEntryDate = this.accountingEntryDate;
      this.generateAccountingEntriesPreview();
    },
    selectedPaymentMethod: 'generateAccountingEntriesPreview',
    paymentsData: 'generateAccountingEntriesPreview',
    abonoId: 'resetFormState',
    idReceipt: 'resetFormState'
  },
  computed: {
    emptyItemsCount() {
      let count = 0;
      this.accountingEntriesPreview.forEach(entry => {
        entry.details.forEach(item => {
          if (!item.concept || !item.concept.trim()) {
            count++;
          }
        });
      });
      return count;
    },
    isFormValid() {
      // Validar que el tipo de partida esté seleccionado
      if (!this.accountingEntryConfig.type) {
        return false;
      }

      // Validar que la fecha esté seleccionada
      if (!this.selectedAccountingEntryDate) {
        return false;
      }

      // Validar que el concepto tenga al menos algún valor (no vacío)
      if (!this.accountingEntryConfig.concept || !this.accountingEntryConfig.concept.trim()) {
        return false;
      }

      // Validar que haya datos de pagos
      if (!this.paymentsData || this.paymentsData.length === 0) {
        return false;
      }

      // Validar que se haya generado una vista previa
      if (!this.accountingEntriesPreview || this.accountingEntriesPreview.length === 0) {
        return false;
      }

      // Validar nombres personalizados si está seleccionado ese modo
      if (this.accountingEntryConfig.nameType === 'empty' && this.emptyItemsCount > 0) {
        return false;
      }

      return true;
    }
  },
  methods: {
    getRowClass(item) {
      return item.id_account === 0 ? 'row-error' : ''
    },
    changeAccountingDate(date) {
      this.selectedAccountingEntryDate = date;
    },
    cancelForm() {
      this.resetFormState();
      this.$emit('close');
    },
    resetFormState() {
      this.loading = false;
      this.alert = {
        type: "success",
        show: false,
        header: "",
        body: "",
      };
      this.accountingEntryConfig = {
        concept: "",
        type: null,
        nameType: "correlativoCodigo"
      };
      this.customItemNames = {};
      this.selectedAccountingEntryDate = this.accountingEntryDate;
      this.accountingEntriesPreview = [];
      this.previewPanel = [0];
      this.closeAfterAcceptAlert = false;
      this.generateAccountingEntriesPreview();
    },
    updateCustomItemName(item, newValue) {
      const key = this.generateItemKey(item);
      this.$set(this.customItemNames, key, newValue);
      this.$set(item, 'concept', newValue);
    },
    async getAcounteringType() {
      try {
        const response = await this.$API.branchaccountcatalog.getAcounteringType();
        this.TypesItems = response.data;
      } catch (error) {
        console.error("Error loading accounting types:", error);
      }
    },
    async getBranchAccountConfig() {
      try {
        const response = await this.$API.branchaccountconfig.getBranchAccountsConfig();
        if (response.data) {
          this.branchAccountConfig = response.data.config;
          this.paymentMethods = response.data.paymentmethods || [];
        }
      } catch (error) {
        console.error("Error loading branch account config:", error);
      }
    },
    async getAcountList() {
      try {
        const result = await this.$API.branchaccountcatalog.getActiveBranchAccountCatalog();
        this.AcountList = result.data;
      } catch (error) {
        console.error("Error loading account list:", error);
      }
    },
    async getLastAccountingCorrelative() {
      try {
        const response = await this.$API.accountlevel.getLastAcountingCorrelative();
        this.lastAccountingCorrelative = response.data.ID_Accounting_item || '';
      } catch (error) {
        console.error("Error loading last correlative:", error);
      }
    },
    async getDetailTypes() {
      try {
        const response = await this.$API.accountingentrydetailtype.getAccountingEntryDetailTypes();
        this.detailTypes = response.data;
      } catch (error) {
        console.error("Error loading detail types:", error);
      }
    },
    async getProviderAccount() {
      try {
        const response = await this.$API.providers.getProviderWithAccounts({ ID_Provider: this.provider.ID_Provider });
        if (response && response.CuentaCuentasPorPagar) {
          this.provider.CuentaCuentasPorPagar = response.CuentaCuentasPorPagar;
        }
      } catch (error) {
        console.error("Error loading provider account:", error);
      }
    },
    generateItemKey(item) {
      return `${item.account_code}_${item.Deb}_${item.Hab}`;
    },
    getCustomItemName(item) {
      const key = this.generateItemKey(item);
      return this.customItemNames[key] || '';
    },
    generateAccountingEntriesPreview() {
      if (!this.paymentsData.length) return;

      this.accountingEntriesPreview = [];

      // Agrupar abonos por orden de compra o crear una partida por abono
      const entry = {
        concept: this.accountingEntryConfig.concept || `Abono a cuentas por pagar - ${this.selectedAccountingEntryDate}`,
        date: this.selectedAccountingEntryDate,
        totalDebe: 0,
        totalHaber: 0,
        details: []
      };

      this.paymentsData.forEach(payment => {
        if (payment.PaymentToBeMade > 0) {
          // Debe: Cuenta de cuentas por pagar
          const liabilityAccount = this.getLiabilityAccount(payment);
          const liabilityDetail = {
            account_code: liabilityAccount.code,
            account_description: liabilityAccount.description,
            id_account: liabilityAccount.id,
            Deb: payment.PaymentToBeMade,
            Hab: 0
          };
          liabilityDetail.concept = this.generateItemConcept(payment, liabilityDetail);
          entry.details.push(liabilityDetail);
          entry.totalDebe += payment.PaymentToBeMade;

          // Haber: Cuenta del método de pago
          const expenseAccount = this.getExpenseAccount(payment);
          const expenseDetail = {
            account_code: expenseAccount.code,
            account_description: expenseAccount.description,
            id_account: expenseAccount.id,
            Deb: 0,
            Hab: payment.PaymentToBeMade
          };
          expenseDetail.concept = this.generateItemConcept(payment, expenseDetail);
          entry.details.push(expenseDetail);
          entry.totalHaber += payment.PaymentToBeMade;
        }
      });

      this.accountingEntriesPreview.push(entry);
    },
    getExpenseAccount(payment) { // eslint-disable-line no-unused-vars
      // Usar la cuenta asignada al método de pago seleccionado
      const paymentMethod = this.selectedPaymentMethod;
      const methodConfig = this.paymentMethods.find(m => m.InvoicePaymentType === paymentMethod);
      const accountId = methodConfig ? methodConfig.id_account : null;
      const account = this.AcountList.find(acc => acc.id_account == accountId);
      const accountCode = account ? account.account_code : '---';
      const result = {
        id: accountId || 0,
        code: accountCode,
        description: account ? account.account_description : `Forma de pago ${paymentMethod} -- SIN CUENTA ASIGNADA --`
      };
      return result;
    },
    getLiabilityAccount(payment) {
      // Priorizar la cuenta de cuentas por pagar asignada al proveedor, si existe
      let accountId, accountCode, description;

      if (this.provider.CuentaCuentasPorPagar && this.provider.CuentaCuentasPorPagar.toString().trim()) {
        // Buscar la cuenta por ID asignado al proveedor
        const account = this.AcountList.find(acc => acc.id_account == this.provider.CuentaCuentasPorPagar);
        if (account) {
          accountId = account.id_account;
          accountCode = account.account_code;
          description = account.account_description || 'Cuentas por Pagar (Proveedor)';
        } else {
          // Fallback si no se encuentra la cuenta
          accountId = null;
          accountCode = '';
          description = 'Proveedor sin cuenta por pagar';
        }
      } else {
        console.log("BRANCH CONFIG:")
        console.log(this.branchAccountConfig)
        // Usar la cuenta general de la sucursal según el tipo de origen
        if (payment.OriginType === 'Gasto') {
          accountId = this.branchAccountConfig?.expense_credit_id_account;
        } else {
          accountId = this.branchAccountConfig?.purchase_credit_id_account;
        }
        const account = this.AcountList.find(acc => acc.id_account == accountId);
        accountCode = account ? account.account_code : '';
        description = account ? account.account_description : 'Proveedor sin cuenta por pagar';
      }

      const result = {
        id: accountId || 0,
        code: accountCode,
        description: description
      };
      return result;
    },
    generateItemConcept(payment, itemData = null) {
      switch (this.accountingEntryConfig.nameType) {
        case 'empty':
          return itemData ? this.getCustomItemName(itemData) : '';
        case 'correlativoCodigo':
          return payment.DocumentNumber || 'Abono';
        case 'nombreProveedor':
          return this.provider.ProviderName || 'Proveedor';
        case 'concept':
          return this.accountingEntryConfig.concept || '';
        default:
          return 'Abono';
      }
    },
    async createAccountingEntry() {
      if (!this.isFormValid) {
        this.showAlert("danger", "Error", "Complete todos los campos requeridos antes de crear la partida");
        return;
      }

      this.loading = true;

      try {
        const payload = {
          accountingentry: {
            // ID_Accounting_item será generado por el backend
            id_accounting_entry_type: this.accountingEntryConfig.type,
            accounting_entry_date: new Date(formatDateV2(this.selectedAccountingEntryDate)),
            accounting_entry_comment: this.accountingEntryConfig.concept,
            accounting_entry_value: this.accountingEntriesPreview[0].totalDebe,
            accountingentrydetails: this.accountingEntriesPreview[0].details.map(detail => ({
              id_account: detail.id_account,
              id_entry_detail_type: detail.Deb > 0 ? this.detailTypes.find(t => t.entry_detail_type_description === "DEBER").id_entry_detail_type :
                                   this.detailTypes.find(t => t.entry_detail_type_description === "HABER").id_entry_detail_type,
              amount: detail.Deb || detail.Hab,
              concept: detail.concept
            }))
          },
          id_abono: this.abonoId,
          id_receipt: this.idReceipt
        };

        await this.$API.accountingentryheader.createAccountingEntryWithAbonoUpdate(payload);

        this.closeAfterAcceptAlert = true;
        this.showAlert("successWithCallback", "Éxito", "Partida contable creada correctamente");
      } catch (error) {
        console.error('Error al crear la partida:', error);
        let errorMessage;
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (typeof error === 'object' && error.message) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else {
          errorMessage = 'Error desconocido al crear la partida';
        }
        this.showAlert("danger", "Error", errorMessage);
      } finally {
        this.loading = false;
      }
    },
    showAlert(type, header, body) {
      this.alert.type = type || "danger";
      this.alert.header = header;
      this.alert.body = body;
      this.alert.show = true;
    },

    closeAlert() {
      this.alert.show = false;
    },
    acceptAlert() {
      this.alert.show = false;
      if (!this.closeAfterAcceptAlert) {
        return;
      }

      this.$emit('accountingEntryCreated');
      this.resetFormState();
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.card-step {
  min-height: 400px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2rem;
}
.btncontinue {
  background-color: rgba(80, 80, 80, 1) !important;
  color: white;
}
::v-deep .row-error {
  background-color: #b9011c !important;
  color: #fcfcfc !important;
}
</style>
